import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { RestService, UploadStatus } from './rest.service';
import { Tuple2 } from '../model.module/tuple2.model';
import { PipeFunctions } from '../pipes.module/_pipe.functions';
import { AppConstants } from '../app-constants.service';

@Injectable()
export class FileUploadService {

    errorMsgIfWrongFile: string;
    errorMsgIfFilesTooMany: string;
    errorMsgIfFileEmpty: string;
    errorMsgIfFileTooLarge: string;
    errorMsgIfFileSumTooLarge: string;
    errorWrongFileType: string;

    restPath: string;
    uploadRunning: boolean = false;
    uploadInfo: string = '';
    filesLeft: number = 0;

    private maxFilesAtOnce: number;
    private maxFileSumSize: number;
    private maxFileSize: number;
    private fileQueue: File[];
    private uploadImmediately: boolean = false;
    private extensionFilterArray: string[] = null;

    private uploadedAction: (filename: string) => void = () => {};

    private progressAction: (progressPercent: number) => void = () => {};

    private addedAction: (files: File[]) => void = () => {};

    private errorAction: (errMsg: string) => void = (errMsg: string) => {
        console.error(errMsg);
    };

    constructor(
        private authService: AuthService,
        private restService: RestService,
        private appConstants: AppConstants
    ) {
        this.errorMsgIfFileEmpty = appConstants.messages.uploadFileEmpty;
        this.errorMsgIfFileTooLarge = appConstants.messages.uploadFileToLarge;
        this.errorMsgIfFilesTooMany = appConstants.messages.uploadFilesToMany;
        this.errorMsgIfWrongFile = appConstants.messages.uploadFilesWrongType;
        this.errorMsgIfFileSumTooLarge = appConstants.messages.uploadFileSumTooLarge;
    }

    forData(
        restPath: string,
        maxFilesAtOnce: number,
        maxFileSize: number,
        maxFileSumSize: number = -1,
        uploadImmediately: boolean = false,
        extensionFilterArray: string[] = null,
        errorWrongFileType: string = null,
        progressAction: (progressPercent: number) => void = null,
        addedAction: (files: File[]) => void = null,
        uploadedAction: (filename: string) => void = null,
        errorAction: (errMsg: string) => void = null
    ): FileUploadService {
        this.restPath = restPath;
        this.maxFilesAtOnce = maxFilesAtOnce;
        this.maxFileSumSize = maxFileSumSize;
        this.maxFileSize = maxFileSize;
        this.uploadImmediately = uploadImmediately;
        this.extensionFilterArray = extensionFilterArray;
        this.errorWrongFileType = errorWrongFileType;
        this.progressAction = progressAction || this.progressAction;
        this.addedAction = addedAction || this.addedAction;
        this.uploadedAction = uploadedAction || this.uploadedAction;
        this.errorAction = errorAction || this.errorAction;

        return this;
    }

    cleanup() {
        this.fileQueue = [];
    }

    private getCookie(name) {
        var value = '; ' + document.cookie;
        var parts = value.split('; ' + name + '=');

        if (parts.length == 2) {
            return parts.pop().split(';').shift();
        }

        return null;
    }

    private readonly filter4Extensions = (filename: string): boolean => {
        let result: boolean = false;

        if (this.extensionFilterArray !== null) {
            this.extensionFilterArray.forEach(ext4 => {
                if (filename.toLowerCase().endsWith(ext4)) {
                    result = true;
                }
            });
        } else {
            return true;
        }

        return result;
    };

    public deleteFileAtIndex(index: number) {
        if (this.fileQueue && this.fileQueue.length > index) {
            this.fileQueue[index] = null;
        }
    }

    private uploadNextHelper(addData: Tuple2<string, string>[] = null, secure: boolean = true): void {
        if (this.fileQueue.length === 0) {
            this.uploadRunning = false;
            return;
        }

        if (this.uploadRunning) {
            return;
        }

        this.uploadRunning = true;
        let file = null;

        do {
            file = this.fileQueue.shift();
        } while (!file && this.fileQueue.length !== 0);
        if (!file) {
            this.uploadRunning = false;
            return;
        }

        this.filesLeft = this.fileQueue.length;
        this.uploadInfo = file.name;
        const formData = new FormData();
        formData.append('uploadFile', file);

        if (secure) {
            const user = this.authService.getActiveKunde().vsnr;
            const roleName = this.authService.getRoleName();
            const activeUser = this.authService.getUserId();

            formData.append('user', user);
            formData.append('roleName', roleName);
            formData.append('activeUser', activeUser);
        }

        if (addData !== null) {
            addData.forEach((ad) => {
                formData.append(ad.left, ad.right);
            });
        }

        let uploadFilename: string = undefined;

        const xsrfToken = this.getCookie('XSRF-TOKEN');

        this.restService.doFileUpload(this.restPath, formData, xsrfToken)
            .subscribe({
                next: (uploadStatus: UploadStatus) => {
                    if (!uploadStatus) {
                        return;
                    }

                    if (uploadStatus.total && uploadStatus.loaded) {
                        this.progressAction(Math.ceil(uploadStatus.total / uploadStatus.loaded * 100));
                    }

                    if (!uploadStatus.filename) {
                        return;
                    }

                    uploadFilename = uploadStatus.filename;

                    this.uploadRunning = false;
                    this.uploadedAction(uploadFilename);
                    this.uploadNext(addData, secure);
                },
                error: (error) => {
                    console.error('Fehler beim Dateiupload', error);

                    this.errorAction(this.errorMsgIfWrongFile.replace(/\$fileName/, file.name));

                    this.progressAction(0);
                    this.uploadRunning = false;

                    // Datei wieder in die FileQueue stecken, damit beim naechsten Versuch die Datei erneut versucht wird hochzuladen
                    this.fileQueue.unshift(file);

                    this.addedAction(this.fileQueue);

                    this.uploadRunning = false;
                    this.uploadedAction(uploadFilename);
                    this.uploadNext(addData, secure);
                }
            });
    }

    private uploadNext(addData: Tuple2<string, string>[] = null, secure = true): void {
        this.uploadNextHelper(addData, secure);
    }

    startUpload(addData: Tuple2<string, string>[] = null, secure = true): void {
        this.uploadNext(addData, secure);
    }

    deleteUpload = (i: number): void => {
        this.fileQueue.splice(i, 1);
        this.addedAction(this.fileQueue);
    };

    getFileCount(): number {
        if (!this.fileQueue) {
            return 0;
        }

        // Liste kann null-Elemente enthalten, daher nur echte Elemente zaehlen
        return this.fileQueue.filter(n => n).length;
    }

    getFileNameOrNull = (index: number): string => {
        if (!this.fileQueue) {
            return null;
        }

        if (this.fileQueue.length >= index + 1 && this.fileQueue[index]) {
            return this.fileQueue[index].name;
        }

        return null;
    };

    addFiles(evt: any, atIndex = -1): void {
        if ('stopPropagation' in evt && 'preventDefault' in evt) {
            evt.stopPropagation();
            evt.preventDefault();
        }

        if (!this.fileQueue) {
            this.fileQueue = [];
        }

        const data = evt['dataTransfer'] || evt.target; // drag&drop or input type="file"

        let len = this.fileQueue.length;
        if (atIndex === -1) {
            len += data.files.length;
        }
        if (this.uploadRunning) {
            len++;
        }

        if (len > this.maxFilesAtOnce) {
            this.errorAction(
                this.errorMsgIfFilesTooMany
                    .replace(/\$numFilesRequest/, len + '')
                    .replace(/\$numFiles/, this.maxFilesAtOnce + '')
            );

            return; // silently
        }

        // Prüfe Summe der Dateigrößen
        if (this.maxFileSumSize !== -1) {
            const queueSize: number = this.fileQueue.reduce(
                (a: number, f: File) => a + (f ? f.size : 0),
                0
            );

            const allSize: number = Array.from(data.files).reduce<number>(
                (a: number, f: any) => a + (f ? f.size : 0),
                queueSize
            );

            if (allSize > this.maxFileSumSize) {
                this.errorAction(this.errorMsgIfFileSumTooLarge
                    .replace(/\$fileSize/, PipeFunctions.bytesToMega(allSize))
                    .replace(/\$max/, PipeFunctions.bytesToMega(this.maxFileSumSize)));

                return;
            }
        }

        // Prüfe Dateigröße der einzelnen Dateien
        if (this.maxFileSize !== -1) {
            for (const file of data.files) {
                if (file.size > this.maxFileSize) {
                    this.errorAction(this.errorMsgIfFileTooLarge.replace(/\$fileName/, file.name)
                        .replace(/\$fileSize/, PipeFunctions.bytesToMega(file.size))
                        .replace(/\$max/, PipeFunctions.bytesToMega(this.maxFileSize)));
                    this.fileQueue = [];

                    return;
                }
            }
        }

        for (const file of data.files) {
            // Prüfe Mindestdateigröße > 0 Byte
            if (file.size <= 0) {
                this.errorAction(this.errorMsgIfFileEmpty.replace(/\$fileName/, file.name));
                this.fileQueue = [];

                return;
            }

            // Prüfe Dateiendung
            if (!this.filter4Extensions(file.name)) {
                this.errorAction(this.errorWrongFileType);
                return;
            }
        }

        if (data.files.length === 1 && atIndex !== -1) {
            this.fileQueue[atIndex] = data.files[0];
        } else {
            for (const file of data.files) {
                this.fileQueue.push(file);
            }
        }

        this.errorAction(null);
        this.addedAction(this.fileQueue);

        if (this.uploadImmediately) {
            this.uploadNext();
        }
    }

}
